import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useRequisition = () => {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [requisitionDetails, setRequisitionDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch all requisitions with optional filters
  const fetchRequisitions = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.departmentId) queryObj.department_id = params.departmentId;
        if (params.status) queryObj.status = params.status;
        if (params.priority) queryObj.priority = params.priority;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.requisitions}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setRequisitions(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch requisitions");
        }
      } catch (error) {
        message.error("Error fetching requisitions");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch requisition details by ID
  const fetchRequisitionDetails = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setRequisitionDetails(response.data);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch requisition details");
          return null;
        }
      } catch (error) {
        message.error("Error fetching requisition details");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Create a new requisition with items
  const createRequisition = useCallback(
    async (data, attachments = []) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("payload", JSON.stringify(data));
        attachments.forEach((file) => formData.append("attachments", file));
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.requisitions,
          formData,
          navigate,
        );

        if (response.ok) {
          message.success("Requisition created successfully");
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create requisition");
          return null;
        }
      } catch (error) {
        message.error("Error creating requisition");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Update a requisition
  const updateRequisition = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Requisition updated successfully");
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update requisition");
          return null;
        }
      } catch (error) {
        message.error("Error updating requisition");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Submit requisition for approval
  const submitRequisition = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionSubmit.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );

        if (response.ok) {
          message.success("Requisition submitted for approval");
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to submit requisition");
          return null;
        }
      } catch (error) {
        message.error("Error submitting requisition");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // HoD approve/reject requisition
  const hodApprove = useCallback(
    async (id, action, rejectionReason = "", notes = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionHodApprove.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { action, rejectionReason, notes },
          navigate,
        );

        if (response.ok) {
          message.success(
            action === "approve"
              ? "Requisition approved by HoD"
              : "Requisition rejected by HoD",
          );
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to process HoD approval");
          return null;
        }
      } catch (error) {
        message.error("Error processing HoD approval");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Finance approve/reject requisition
  const financeApprove = useCallback(
    async (id, action, rejectionReason = "", notes = "") => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.finance.requisitionFinanceApprove.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { action, rejectionReason, notes },
          navigate,
        );

        if (response.ok) {
          message.success(
            action === "approve"
              ? "Requisition approved by Finance"
              : "Requisition rejected by Finance",
          );
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to process Finance approval");
          return null;
        }
      } catch (error) {
        message.error("Error processing Finance approval");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Mark requisition as fulfilled
  const markFulfilled = useCallback(
    async (id, notes = "") => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.finance.requisitionMarkFulfilled.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { notes },
          navigate,
        );

        if (response.ok) {
          message.success("Requisition marked as fulfilled");
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to mark requisition as fulfilled",
          );
          return null;
        }
      } catch (error) {
        message.error("Error marking requisition as fulfilled");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Cancel requisition
  const cancelRequisition = useCallback(
    async (id, notes = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionCancel.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { notes },
          navigate,
        );

        if (response.ok) {
          message.success("Requisition cancelled");
          fetchRequisitions();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to cancel requisition");
          return null;
        }
      } catch (error) {
        message.error("Error cancelling requisition");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitions],
  );

  // Add items to requisition
  const addItems = useCallback(
    async (id, items) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.requisitionAddItems.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { items },
          navigate,
        );

        if (response.ok) {
          message.success("Items added to requisition");
          fetchRequisitionDetails(id);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to add items");
          return null;
        }
      } catch (error) {
        message.error("Error adding items");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchRequisitionDetails],
  );

  // Delete requisition
  const deleteRequisition = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.finance.requisitionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Requisition deleted successfully");
          fetchRequisitions();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete requisition");
          return false;
        }
      } catch (error) {
        message.error("Error deleting requisition");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchRequisitions],
  );

  // Get requisition statistics
  const getStatistics = useCallback(() => {
    return {
      total: requisitions.length,
      draft: requisitions.filter((r) => r.status === "Draft").length,
      submitted: requisitions.filter((r) => r.status === "Submitted").length,
      hodApproved: requisitions.filter((r) => r.status === "HoD Approved")
        .length,
      financeApproved: requisitions.filter(
        (r) => r.status === "Finance Approved",
      ).length,
      inProcurement: requisitions.filter((r) => r.status === "Procurement")
        .length,
      fulfilled: requisitions.filter((r) => r.status === "Fulfilled").length,
      rejected: requisitions.filter((r) => r.status.includes("Rejected"))
        .length,
      totalAmount: requisitions.reduce(
        (sum, r) => sum + parseFloat(r.totalAmount || 0),
        0,
      ),
    };
  }, [requisitions]);

  return {
    requisitions,
    requisitionDetails,
    loading,
    fetchRequisitions,
    fetchRequisitionDetails,
    createRequisition,
    updateRequisition,
    submitRequisition,
    hodApprove,
    financeApprove,
    markFulfilled,
    cancelRequisition,
    addItems,
    deleteRequisition,
    getStatistics,
  };
};

export default useRequisition;
